import { useEffect, useRef, useState } from 'preact/hooks';

import SliderStrip, { type SliderStripProps } from 'Components/slider/sliderStrip';
import type { TemplateResult } from 'Components/types';
import { replaceManyValues } from 'Core/actions/request';
import { useIdleEffect } from 'Core/hooks/index';
import { useDispatch } from 'Core/store';
import { FacetValue } from 'Models/index';
import type Step from 'Models/step';
import type { FacetSliderMode } from 'Models/uiConfig/facetsConfig';
import { isTruthy } from 'Utils/guards';
import { convertDecimalToImperial, getNumPercent, getSliderBounds, getSliderRangeTerm } from 'Utils/ranged';

interface Props {
  field: string;
  bounds: [string, string];
  selected: [string, string];
  step: Step;
  mode?: FacetSliderMode;
}

export default function Slider({
  field,
  bounds,
  selected: origSelected,
  step,
  mode: origMode = 'Range',
}: Props): TemplateResult {
  const dispatch = useDispatch();
  const sliderBounds = getSliderBounds(bounds, step);
  const [selected, setSelected] = useState<number[]>(origSelected.map(norm));
  const activeRef = useRef(false);
  const selRef = useRef(selected);
  const startedSelectedRef = useRef(selected);
  const rootRef = useRef<HTMLDivElement>(null);
  const titlesRef = useRef<(HTMLDivElement | null)[]>([]);

  let mode = origMode;

  useEffect(() => {
    selRef.current = selected;
  }, [selected]);

  useIdleEffect(() => {
    titleStyles().forEach(({ left = null, right = null }, i) => {
      const elem = titlesRef.current[i];
      if (elem) {
        elem.style.right = appendPxToTruthy(right);
        elem.style.left = appendPxToTruthy(left);
      }
    });
  });

  function norm(value: string, i: number): number {
    return value === '*' || bounds[0] === bounds[1] ? i * 100 : getNumPercent(+value, sliderBounds);
  }
  if (!activeRef.current) {
    const newSelected = origSelected.map(norm);
    if (newSelected.some(isNaN)) {
      throw new Error('New selected for Slider is NaN');
    }
    if (newSelected[0] !== selected[0] || newSelected[1] !== selected[1]) {
      setSelected(newSelected);
    }
    if (bounds[0] === bounds[1]) {
      mode = 'Lock';
    }
  }

  function titleStyles(): Array<{ left?: number; right?: number }> {
    const titles = [titlesRef.current[0], titlesRef.current[1]];
    if (!titles.some(isTruthy)) {
      return [];
    }
    if (!rootRef.current) {
      return [];
    }
    const widths = titles.map((title) => title?.offsetWidth ?? 0);
    const sliderWidth = rootRef.current.offsetWidth;

    const borders = widths.map((w) => (100 / sliderWidth) * w);
    borders[1] = 100 - borders[1];

    return [
      selected[0] < borders[0]
        ? { left: 0 }
        : selected[0] > borders[1]
          ? { right: widths[1] }
          : { right: sliderWidth - (selected[0] / 100) * sliderWidth },

      selected[1] > borders[1]
        ? { right: 0 }
        : selected[1] < borders[0]
          ? { left: widths[0] }
          : { left: (selected[1] / 100) * sliderWidth },
    ];
  }

  const selectedRange = getSliderRangeTerm(selected, bounds, step);
  const titles = selectedRange.map((value, i) => {
    if ((mode === 'UpperBound' && i === 0) || (mode === 'LowerBound' && i === 1) || mode === 'Lock') {
      return null;
    }
    const isWildcard = value === '*';
    const labelValue = isWildcard ? bounds[i] : value;
    const titleProps = {
      className: `cm_slider-title${activeRef.current && !isWildcard ? ' visible' : ''}`,
      ref: (elem: HTMLDivElement | null) => {
        titlesRef.current[i] = elem;
      },
    };
    return (
      <div {...titleProps} key={`${field}_SlTtl${['Lt', 'Rt'][i]}`}>
        {step.imperial ? convertDecimalToImperial(labelValue) : labelValue}
      </div>
    );
  });

  const onChangeFinished = () => {
    activeRef.current = false;

    if (selRef.current.every((value, i) => value === startedSelectedRef.current[i])) {
      return;
    }

    const sel = getSliderRangeTerm(selRef.current, bounds, step);
    if (sel[0] !== origSelected[0] || sel[1] !== origSelected[1]) {
      const term = FacetValue.createRangedTerm(sel[0], sel[1]);
      const isSelected = sel.some((s) => s !== '*');
      dispatch(
        replaceManyValues([{ field, term, isSelected }], {
          mayDiscardValue: true,
        }),
      );
    }
  };

  const stripProps = {
    field,
    selected,
    mode,
    onChangeStarted: () => {
      activeRef.current = true;
      startedSelectedRef.current = [...selRef.current];
    },
    onChange: (newSelected: number[]) => {
      selRef.current = newSelected;
      setSelected(newSelected);
    },
    onChangeFinished,
  } satisfies SliderStripProps;
  const strip = <SliderStrip key={`sliderstrip-for-${field}`} {...stripProps} />;

  const props = {
    className: 'cm_slider-container',
    ref: rootRef,
  };
  return (
    <div {...props}>
      {titles}
      {strip}
    </div>
  );
}

function appendPxToTruthy(value: number | null | undefined): string {
  return value !== null && value !== undefined ? `${value}px` : '';
}
